import { environment } from '../../environments/environment';

export type TotemNavigationMessage = {
  type: 'navigation';
  url: string;
};

export type TotemActivityMessage = {
  type: 'activity';
};

const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'touchmove',
  'click',
  'scroll',
  'input',
] as const;

let lastActivityAt = 0;

function resolveParentOrigin(): string | null {
  try {
    const ancestorOrigins = (location as Location & { ancestorOrigins?: DOMStringList })
      .ancestorOrigins;
    if (ancestorOrigins && ancestorOrigins.length > 0) {
      return ancestorOrigins[0];
    }
  } catch {
    // ignore
  }

  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // ignore
  }

  return null;
}

function postToTotemParent(payload: TotemNavigationMessage | TotemActivityMessage): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.parent === window) {
    return;
  }

  const allowedOrigins: string[] = environment.totemParentOrigins ?? [];
  if (!allowedOrigins.length) {
    return;
  }

  const parentOrigin = resolveParentOrigin();
  const targets =
    parentOrigin && allowedOrigins.includes(parentOrigin)
      ? [parentOrigin]
      : allowedOrigins;

  for (const target of targets) {
    window.parent.postMessage(payload, target);
  }
}

/**
 * Notifies the kappta-device totem parent frame of an in-iframe route change.
 * Posts to the resolved parent origin when allowlisted; otherwise falls back to
 * every origin in totemParentOrigins (covers empty referrer / 127.0.0.1).
 */
export function notifyTotemNavigation(url?: string): void {
  postToTotemParent({
    type: 'navigation',
    url: url ?? window.location.href,
  });
}

/**
 * Tells the parent totem the user is still interacting inside the iframe.
 * Cross-origin parents cannot see these events themselves. Throttled to ~1s.
 */
export function notifyTotemActivity(): void {
  const now = Date.now();
  if (now - lastActivityAt < ACTIVITY_THROTTLE_MS) {
    return;
  }
  lastActivityAt = now;
  postToTotemParent({ type: 'activity' });
}

/** Listens for in-iframe interaction and forwards `{ type: "activity" }` to the parent. */
export function startTotemActivityBridge(): () => void {
  if (typeof window === 'undefined' || window.parent === window) {
    return () => undefined;
  }

  const onActivity = () => notifyTotemActivity();
  for (const eventName of ACTIVITY_EVENTS) {
    window.addEventListener(eventName, onActivity, { capture: true, passive: true });
  }

  return () => {
    for (const eventName of ACTIVITY_EVENTS) {
      window.removeEventListener(eventName, onActivity, { capture: true });
    }
  };
}
